import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { markTutoPhaseSeen } from '../Api/authApi.js.js';
import { updateUser } from '../redux/authSlice';
import { tutosConfig } from './tutosConfig';

const SCRIM_COLOR = 'rgba(5,7,18,0.6)';

// Carte de position fictive affichée dans le drawer (visuals avec `mockPosition: true`) — reprend le
// visuel de PositionCard (pages/portefeuille/index.js), en autonome puisque ce composant n'y est pas
// exporté. Sert à illustrer le détail d'une position ouverte même si le joueur n'en a pas encore une.
function MockMetric({ label, value, color }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 9, color: '#5C6B88', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {label}
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: color || '#F2F5FB' }}>{value}</span>
        </div>
    );
}

function MockPositionPreview() {
    return (
        <div style={{
            marginTop: 14,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14,
            padding: 12,
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                    background: 'rgba(224,194,129,0.12)', border: '1px solid rgba(224,194,129,0.3)',
                    display: 'grid', placeItems: 'center', color: '#E0C281', fontWeight: 700, fontSize: 11,
                }}>BTC</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>Bitcoin</span>
                        <span style={{
                            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                            background: 'rgba(74,222,158,0.12)', color: '#4ADE9E',
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                        }}>Long</span>
                        <span style={{
                            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                            background: 'rgba(224,194,129,0.12)', color: '#E0C281',
                        }}>×3</span>
                    </div>
                    <div style={{ fontSize: 10.5, color: '#5C6B88', marginTop: 2 }}>0.012 · entrée 41 250,50 €</div>
                </div>
            </div>

            <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8,
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10, padding: '8px 10px',
            }}>
                <MockMetric label="Investi" value="500,00 €" />
                <MockMetric label="P&L" value="+42,00 €" color="#4ADE9E" />
                <MockMetric label="Valeur" value="542,00 €" />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
                <div style={{
                    flex: 1, height: 32, borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                    display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 600, color: '#F2F5FB',
                }}>Modifier</div>
                <div style={{
                    flex: 1, height: 32, borderRadius: 8, background: '#fff',
                    display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700, color: '#0A1226',
                }}>Fermer +42,00 €</div>
            </div>

            <div style={{ fontSize: 9.5, color: '#5C6B88', textAlign: 'center', fontStyle: 'italic' }}>
                Exemple
            </div>
        </div>
    );
}

function interpolate(text, vars) {
    if (!text) return text;
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (vars[key] ?? ''));
}

// Un même visuel peut correspondre à plusieurs ids candidats (ex. la cloche notifications existe
// en double dans le DOM — une version desktop, une mobile — seule une des deux est visible selon
// la largeur d'écran). On prend le premier candidat effectivement visible (taille non nulle).
function resolveVisibleElement(elementId) {
    const ids = Array.isArray(elementId) ? elementId : [elementId];
    for (const id of ids) {
        const el = id && document.getElementById(id);
        if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) return el;
        }
    }
    return null;
}

// Un élément (ou un de ses ancêtres) en position fixed ne bouge jamais avec le scroll du document —
// c'est le cas de la cloche notifications mobile, dans la top bar qui se masque elle-même par
// translateY quand on scrolle vers le bas (Drawer.css `.scrolling-bottom`). `scrollIntoView`, voire
// `window.scrollTo`, ne le "rapprochent" jamais de façon fiable puisque sa visibilité ne dépend pas
// du scroll mais de cette classe CSS pilotée par un state (isScrollingDown) qui vit dans un composant
// tiers (Layout) : on force donc sa top bar à rester visible via une classe globale le temps du
// visuel, plutôt que de dépendre du timing du scroll pour la faire réapparaître (voir Drawer.css
// `.tuto-highlight-fixed-visible`).
const TUTO_FORCE_FIXED_VISIBLE_CLASS = 'tuto-highlight-fixed-visible';
const DRAWER_MARGIN = 16;

function isInsideFixedElement(el) {
    let node = el;
    while (node && node !== document.body) {
        if (window.getComputedStyle(node).position === 'fixed') return true;
        node = node.parentElement;
    }
    return false;
}

// La fiche actif (fiche_actif_intro) a son propre conteneur de contenu scrollable — modal plein
// écran sur mobile, panel latéral sur desktop (`overflowY: 'auto'` dans TradeModal) — indépendant du
// scroll de la fenêtre, qui est même verrouillé (`body.overflow = 'hidden'`) tant que la modal mobile
// est ouverte. Un élément à l'intérieur ne bouge donc JAMAIS avec `window`, seulement avec ce
// conteneur : il faut le détecter pour scroller et suivre le bon élément, pas la fenêtre.
function findScrollableAncestor(el) {
    let node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const canScrollY = (style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1;
        if (canScrollY) return node;
        node = node.parentElement;
    }
    return null;
}

function getMaxScrollY(scroller) {
    if (scroller) return Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const doc = document.scrollingElement || document.documentElement;
    return Math.max(0, doc.scrollHeight - window.innerHeight);
}

function getCurrentScrollY(scroller) {
    return scroller ? scroller.scrollTop : window.scrollY;
}

function scrollByAmount(scroller, amount) {
    (scroller || window).scrollBy({ top: amount, behavior: 'smooth' });
}

// Le drawer est par défaut collé en bas de l'écran, ce qui masque le tiers inférieur du viewport.
// Un élément situé tout en bas de ce qui peut être scrollé (fenêtre, ou conteneur interne comme le
// contenu de la fiche actif) ne peut alors jamais en être dégagé, même en scrollant au maximum : dans
// ce cas on bascule le drawer en haut à la place.
function pickDrawerPosition(el, drawerHeight, scroller) {
    const maxScrollY = getMaxScrollY(scroller);
    const elBottomAbs = el.getBoundingClientRect().bottom + getCurrentScrollY(scroller);
    const bottomSafeLimit = maxScrollY + window.innerHeight - drawerHeight - DRAWER_MARGIN;
    return elBottomAbs > bottomSafeLimit ? 'top' : 'bottom';
}

// `scrollIntoView` juge un élément "déjà visible" dès qu'il est dans le viewport géométrique, sans
// savoir qu'une partie de ce viewport est en fait couverte par le drawer du tuto : un élément assis
// dans cette zone ne scrolle donc jamais alors qu'il reste masqué. On calcule ici la zone "sûre"
// (viewport moins le drawer, plus une marge, du côté opposé au drawer) et on recentre toujours
// l'élément dedans (pas de "déjà bien placé, on ne fait rien" — un mauvais calcul de visibilité ne
// doit jamais se traduire par une absence totale de scroll). Scrolle le conteneur concerné
// (`scroller`) plutôt que systématiquement la fenêtre.
function scrollElementIntoSafeView(el, drawerHeight, drawerPosition, scroller) {
    const rect = el.getBoundingClientRect();
    const safeTop = drawerPosition === 'top' ? drawerHeight + DRAWER_MARGIN : DRAWER_MARGIN;
    const safeBottom = drawerPosition === 'top'
        ? window.innerHeight - DRAWER_MARGIN
        : Math.max(safeTop + 80, window.innerHeight - drawerHeight - DRAWER_MARGIN);

    const safeCenter = (safeTop + safeBottom) / 2;
    const elCenter = (rect.top + rect.bottom) / 2;
    scrollByAmount(scroller, elCenter - safeCenter);
}

// Position du highlight dans le document pour un élément normal (dans le flux de la fenêtre, pas
// fixed, pas dans un conteneur scrollable imbriqué) : ses coordonnées ABSOLUES (rect.top +
// window.scrollY) sont invariantes au scroll — contrairement à des coordonnées viewport, qu'il
// faudrait retracker en continu (source du décrochage pendant un scroll tactile actif, notamment sur
// iOS Safari). Mesurer une fois suffit : le highlight, rendu en position absolute, défilera ensuite
// nativement avec la page.
function measureAbsolute(el) {
    const r = el.getBoundingClientRect();
    return { top: r.top + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height };
}

function measureViewport(el) {
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
}

// Détermine la position (viewport ou document) de l'élément mis en avant, et l'amène dans le champ
// de vision si besoin (masqué par le drawer, top bar mobile qui se cache au scroll, ou conteneur
// scrollable imbriqué comme la fiche actif). `drawerRef` pointe vers le drawer lui-même, pour
// connaître sa hauteur actuelle.
function useHighlightRect(elementId, drawerRef) {
    const [rect, setRect] = useState(null);
    const [fixed, setFixed] = useState(false);
    const [drawerPosition, setDrawerPosition] = useState('bottom');

    useEffect(() => {
        if (!elementId) {
            setRect(null);
            return undefined;
        }

        const el = resolveVisibleElement(elementId);
        if (!el) {
            setRect(null);
            return undefined;
        }

        // Le conteneur scrollable imbriqué est cherché en premier : sur mobile, la fiche actif
        // (#trade-modal-root) est ELLE-MÊME en position fixed (plein écran) tout en ayant un contenu
        // scrollable interne — si on testait `isInsideFixedElement` en premier, on tomberait sur ce
        // fixed avant même de chercher le conteneur scrollable, et on partirait à tort sur la branche
        // "cloche notifications" (sur desktop le panel n'est que `relative`, d'où la différence de
        // comportement observée entre les deux). Un conteneur scrollable imbriqué prime donc toujours
        // sur un simple ancêtre fixed.
        const nestedScroller = findScrollableAncestor(el);
        const isFixedNav = !nestedScroller && isInsideFixedElement(el);
        // Dans les deux cas suivants, le scroll de `window` ne fait pas bouger l'élément (la cloche
        // vit dans une top bar fixed ; la fiche actif dans son propre conteneur scrollable) : il faut
        // donc suivre sa position viewport en direct plutôt que la position "absolute document" (qui
        // ne serait invariante que par rapport au scroll de `window`, pas d'un conteneur imbriqué).
        const useViewportTracking = isFixedNav || Boolean(nestedScroller);
        setFixed(useViewportTracking);

        if (isFixedNav) {
            // Position viewport : ne bouge jamais avec le scroll (c'est un élément fixed), mais sa
            // visibilité dépend d'une transition CSS tierce (top bar qui se démasque, 300ms) qu'il
            // faut laisser retomber avant de lire sa position définitive.
            setDrawerPosition('bottom');
            document.body.classList.add(TUTO_FORCE_FIXED_VISIBLE_CLASS);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            const update = () => setRect(measureViewport(el));
            update();
            const settleTimeout = setTimeout(update, 350);
            window.addEventListener('resize', update);

            return () => {
                clearTimeout(settleTimeout);
                window.removeEventListener('resize', update);
                document.body.classList.remove(TUTO_FORCE_FIXED_VISIBLE_CLASS);
            };
        }

        if (nestedScroller) {
            const drawerHeight = drawerRef?.current?.getBoundingClientRect().height || 0;
            const position = pickDrawerPosition(el, drawerHeight, nestedScroller);
            setDrawerPosition(position);
            scrollElementIntoSafeView(el, drawerHeight, position, nestedScroller);

            const update = () => setRect(measureViewport(el));
            update();
            const settleTimeout = setTimeout(update, 350);
            nestedScroller.addEventListener('scroll', update, { passive: true });
            window.addEventListener('resize', update);

            return () => {
                clearTimeout(settleTimeout);
                nestedScroller.removeEventListener('scroll', update);
                window.removeEventListener('resize', update);
            };
        }

        // Élément normal, scrollé par la fenêtre : sa position absolue ne change pas quand on
        // scrolle (ni notre propre scroll programmatique, ni un scroll manuel) — une seule mesure suffit.
        const drawerHeight = drawerRef?.current?.getBoundingClientRect().height || 0;
        const position = pickDrawerPosition(el, drawerHeight, null);
        setDrawerPosition(position);
        scrollElementIntoSafeView(el, drawerHeight, position, null);

        const update = () => setRect(measureAbsolute(el));
        update();
        window.addEventListener('resize', update);

        return () => window.removeEventListener('resize', update);
    }, [elementId, drawerRef]);

    return { rect, fixed, drawerPosition };
}

function TutoDrawer({ phase, username, onClose }) {
    const [stepIndex, setStepIndex] = useState(0);
    const visual = phase.visuals[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === phase.visuals.length - 1;
    const drawerRef = useRef(null);
    const { rect: highlightRect, fixed: highlightFixed, drawerPosition } = useHighlightRect(visual?.highlightedElementId, drawerRef);
    const isTop = drawerPosition === 'top';

    return createPortal(
        <>
            <style>{`
                @keyframes tutoDrawerIn {
                    from { opacity: 0; transform: translateY(100%); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes tutoDrawerInTop {
                    from { opacity: 0; transform: translateY(-100%); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* Bloque toute interaction avec la page derrière le tuto (clic, ouverture du menu,
                navigation...) tant qu'une étape est affichée. Distinct de la découpe "spotlight"
                ci-dessous : celle-ci n'est qu'un box-shadow, purement visuel, qui ne peut pas
                intercepter les clics quel que soit son pointerEvents. */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 9996 }} />

            {/* Fond assombri, avec découpe "spotlight" autour de l'élément mis en avant s'il y en a un */}
            {highlightRect ? (
                <div style={{
                    position: highlightFixed ? 'fixed' : 'absolute',
                    top: highlightRect.top - 4, left: highlightRect.left - 4,
                    width: highlightRect.width + 8, height: highlightRect.height + 8,
                    outline: '4px solid #FFFFFF',
                    background: 'rgba(255,255,255,0.3)',
                    boxShadow: `0 0 0 9999px ${SCRIM_COLOR}`,
                    borderRadius: 10,
                    pointerEvents: 'none',
                    zIndex: 9997,
                    transition: 'top .2s ease, left .2s ease, width .2s ease, height .2s ease',
                }} />
            ) : (
                <div style={{ position: 'fixed', inset: 0, background: SCRIM_COLOR, zIndex: 9997 }} />
            )}

            {/* Drawer — collé en bas par défaut, basculé en haut quand l'élément mis en avant est trop
                bas dans la page pour être dégagé d'un drawer bas (plus rien à scroller en dessous) */}
            <div style={{
                position: 'fixed', left: 0, right: 0, zIndex: 9998,
                display: 'flex', justifyContent: 'center',
                ...(isTop ? { top: 0 } : { bottom: 0 }),
            }}>
                <div ref={drawerRef} style={{
                    width: '100%', maxWidth: 480,
                    background: 'linear-gradient(180deg, rgba(24,39,68,0.38) 0%, #0E1830 38%), #14213D',
                    border: '1px solid rgba(255,255,255,0.07)',
                    ...(isTop ? {
                        borderTop: 'none',
                        borderRadius: '0 0 24px 24px',
                        boxShadow: '0 20px 50px -12px rgba(0,0,0,0.65)',
                        padding: 'calc(18px + env(safe-area-inset-top, 0px)) 20px 18px',
                    } : {
                        borderBottom: 'none',
                        borderRadius: '24px 24px 0 0',
                        boxShadow: '0 -20px 50px -12px rgba(0,0,0,0.65)',
                        padding: '18px 20px calc(18px + env(safe-area-inset-bottom, 0px))',
                    }),
                    color: '#FFFFFF',
                    animation: `${isTop ? 'tutoDrawerInTop' : 'tutoDrawerIn'} .32s cubic-bezier(0.16, 1, 0.3, 1)`,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ fontSize: 11, letterSpacing: '0.08em', color: '#97A4BE', textTransform: 'uppercase' }}>
                            {stepIndex + 1} / {phase.visuals.length}
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                fontSize: 12.5, fontWeight: 600, color: '#97A4BE', textDecoration: 'underline',
                            }}
                        >
                            Ignorer
                        </button>
                    </div>

                    <h3 style={{ margin: '10px 0 6px', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                        {interpolate(visual.title, { username })}
                    </h3>
                    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: '#D7DDEA' }}>
                        {interpolate(visual.description, { username })}
                    </p>

                    {visual.mockPosition && <MockPositionPreview />}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, gap: 10 }}>
                        <button
                            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                            disabled={isFirst}
                            aria-label="Précédent"
                            style={{
                                width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                                color: isFirst ? '#3E4A63' : '#FFFFFF', cursor: isFirst ? 'default' : 'pointer',
                                display: 'grid', placeItems: 'center',
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>

                        <div style={{ display: 'flex', gap: 6, flex: 1, justifyContent: 'center' }}>
                            {phase.visuals.map((_, i) => (
                                <span key={i} style={{
                                    width: i === stepIndex ? 18 : 6, height: 6, borderRadius: 3,
                                    background: i === stepIndex ? '#FFFFFF' : 'rgba(255,255,255,0.25)',
                                    transition: 'width .2s ease',
                                }} />
                            ))}
                        </div>

                        {isLast ? (
                            <button
                                onClick={onClose}
                                style={{
                                    height: 44, padding: '0 20px', borderRadius: 14, flexShrink: 0,
                                    background: 'linear-gradient(180deg, #ffffff 0%, #e3e8f1 100%)',
                                    color: '#0A1226', border: 'none', cursor: 'pointer',
                                    fontWeight: 700, fontSize: 14,
                                }}
                            >
                                Fermer
                            </button>
                        ) : (
                            <button
                                onClick={() => setStepIndex((i) => Math.min(phase.visuals.length - 1, i + 1))}
                                aria-label="Suivant"
                                style={{
                                    width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                                    color: '#FFFFFF', cursor: 'pointer',
                                    display: 'grid', placeItems: 'center',
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

// Détecte la phase de tuto encore non vue par le joueur connecté (déclenchée soit par la route
// courante, soit — pour un écran qui n'est pas une page à part entière, ex. une fiche ouverte en
// modal — par l'apparition d'un élément précis dans le DOM), la marque comme vue (seenAt) puis
// l'affiche. Une phase ne s'affiche donc jamais deux fois pour un même joueur. À monter une seule
// fois pour les joueurs (pas les professeurs), ex. dans Layout.
const TutoRunner = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [activePhase, setActivePhase] = useState(null);
    const triggeredRef = useRef(new Set());

    // Miroir toujours à jour de user/activePhase, lisible depuis le callback du MutationObserver
    // ci-dessous (mis en place une seule fois, il ne doit pas travailler sur des valeurs figées).
    const latestRef = useRef({ user, activePhase });
    useEffect(() => {
        latestRef.current = { user, activePhase };
    });

    // "Revoir un tuto" (page Profil) retire une phase de tutos_seen sans démonter TutoRunner : il
    // faut alors l'effacer de triggeredRef pour qu'elle puisse se redéclencher (sinon elle reste
    // bloquée pour le reste de la session, triggeredRef n'étant sinon jamais nettoyé).
    useEffect(() => {
        for (const phase of tutosConfig) {
            if (!user?.tutos_seen?.[phase.name]?.seenAt) {
                triggeredRef.current.delete(phase.name);
            }
        }
    }, [user?.tutos_seen]);

    const triggerPhase = (phase) => {
        const currentUser = latestRef.current.user;
        if (!currentUser?.id) return;

        triggeredRef.current.add(phase.name);

        markTutoPhaseSeen(currentUser.id, phase.name)
            .then((data) => {
                dispatch(updateUser({ tutos_seen: data.tutos_seen }));
                setActivePhase(phase);
            })
            .catch(() => {
                // On retentera à la prochaine occasion qui déclenche cette phase.
                triggeredRef.current.delete(phase.name);
            });
    };

    // Déclenchement par URL — la plupart des écrans : une phase = une page.
    useEffect(() => {
        if (!user?.id || activePhase) return;

        const phase = tutosConfig.find((p) => p.page && location.pathname.startsWith(p.page));
        if (!phase || triggeredRef.current.has(phase.name)) return;

        const alreadySeen = Boolean(user.tutos_seen?.[phase.name]?.seenAt);
        if (alreadySeen) return;

        triggerPhase(phase);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, user, activePhase, dispatch]);

    // Déclenchement par apparition d'un élément dans le DOM — pour un écran qui n'est pas une page
    // à part entière (ex. la fiche d'un actif, ouverte en modal par-dessus /portefeuille_menu).
    useEffect(() => {
        const elementPhases = tutosConfig.filter((p) => p.triggerElementId);
        if (elementPhases.length === 0) return undefined;

        const checkAll = () => {
            if (latestRef.current.activePhase) return;
            const currentUser = latestRef.current.user;
            if (!currentUser?.id) return;

            for (const phase of elementPhases) {
                if (triggeredRef.current.has(phase.name)) continue;

                const alreadySeen = Boolean(currentUser.tutos_seen?.[phase.name]?.seenAt);
                if (alreadySeen) {
                    triggeredRef.current.add(phase.name);
                    continue;
                }

                if (document.getElementById(phase.triggerElementId)) {
                    triggerPhase(phase);
                    break;
                }
            }
        };

        const observer = new MutationObserver(checkAll);
        observer.observe(document.body, { childList: true, subtree: true });
        checkAll();

        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!activePhase) return null;

    return (
        <TutoDrawer
            phase={activePhase}
            username={user?.username || ''}
            onClose={() => setActivePhase(null)}
        />
    );
};

export default TutoRunner;
